'use client'

import { useIntl } from 'react-intl'
import { Cell, Text, Icon } from '@vinted/web-ui'
import { CancelCircle24, CheckCircle24 } from '@vinted/monochrome-icons'

import {
  RefundProgressDetailsModel,
  RefundProgressStatus,
} from '@marketplace-web/order-details/order-details-data'

type Props = {
  details: RefundProgressDetailsModel
}

const Details = ({ details }: Props) => {
  const { formatDate, formatTime } = useIntl()

  const renderDate = (date: string, showTime?: boolean) => {
    return (
      <>
        {formatDate(date, { year: 'numeric', month: 'long', day: 'numeric' })}
        {showTime && `, ${formatTime(date)}`}
      </>
    )
  }

  const renderNote = (muted?: boolean) => {
    if (!details.note) return null

    return (
      <Text as="h4" text={details.note} type="caption" theme={muted ? 'muted' : undefined} html />
    )
  }

  const renderCompletedDetails = () => {
    return (
      <Cell
        prefix={<Icon name={CheckCircle24} color="primary-default" />}
        title={<Text as="h2" text={details.title} type="title" />}
        body={<Text as="span" text={renderDate(details.date, details.exactDate)} />}
        validation={renderNote()}
      />
    )
  }

  const renderPendingDetails = () => {
    return (
      <Cell
        prefix={<Icon name={CheckCircle24} color="greyscale-level-3" />}
        title={<Text as="h2" text={details.title} type="title" theme="muted" />}
        body={<Text as="span" text={renderDate(details.date, details.exactDate)} theme="muted" />}
        validation={renderNote(true)}
      />
    )
  }

  const renderFailedDetails = () => {
    return (
      <Cell
        prefix={<Icon name={CancelCircle24} color="warning-default" />}
        title={<Text as="h2" text={details.title} type="title" theme="warning" />}
        body={<Text as="span" text={renderDate(details.date, details.exactDate)} />}
        validation={renderNote()}
      />
    )
  }

  if (!details) return null

  switch (details.status) {
    case RefundProgressStatus.Completed:
      return renderCompletedDetails()
    case RefundProgressStatus.Pending:
      return renderPendingDetails()
    case RefundProgressStatus.Failed:
      return renderFailedDetails()
    default:
      return null
  }
}

export default Details
